import { forgetUndo } from '@/src/renderer/lib/undo'
import type { DataSource } from './dataSource'

/**
 * **Der Wächter für `⌘Z`** (ADR 0028): Jede Änderung, die nicht selbst eine
 * Rücknahme anbietet, lässt **die gemerkte Handlung** vergessen.
 *
 * Ohne ihn nähme `⌘Z` etwas zurück, das hinter der letzten Handlung liegt:
 * löschen, zwei Minuten später umbenennen, `⌘Z` — und das Gelöschte wäre
 * wieder da. Nur wenige Handlungen melden sich an (ADR 0028: acht, seit #204
 * dazu Einhängen, Herauslösen, Umhängen); alles andere (Titel, Datum, Tags,
 * Reihenfolge, Verschieben, Anlegen) tut es nicht.
 *
 * **Hier und nicht an den Aufrufern**, weil `DataSource` die eine Stelle ist,
 * durch die jeder Nutzer-Schreibvorgang geht. Der **Sync** geht daran vorbei
 * (direkt auf `db`) — er lässt also nichts vergessen, und ein Abgleich vom
 * anderen Gerät nimmt niemandem sein `⌘Z`.
 *
 * **Die Ausnahmeliste ist umgekehrt gebaut:** Vergessen lässt jede Methode,
 * außer den hier benannten. Wer morgen eine Schreibmethode ergänzt und nicht
 * an `⌘Z` denkt, bekommt damit das sichere Verhalten — die Taste schweigt,
 * statt etwas Altes zurückzunehmen. Eine Liste, in die man sich eintragen
 * muss, verfehlt genau den, der nicht daran denkt.
 *
 * Daraus folgt eine Regel für die Aufrufer: **Wer sich anmeldet, tut es als
 * Letztes**, nach seinen eigenen Schreibvorgängen.
 */

/** Lesen ändert nichts. Die Namen folgen der Hausordnung der Schnittstelle. */
const READ_PREFIXES = ['use', 'get', 'load', 'search', 'find']

/**
 * Schreibt, lässt aber bewusst nicht vergessen:
 *
 * - `setCanvasNoteMeasuredHeight` / `setCanvasNoteMeasuredSize` — ein Messwert,
 *   keine Handlung (die Schnittstelle sagt es selbst: „a fact about rendering,
 *   not a user decision")
 * - `recordRewardUsage` — Nebenwirkung des Auswählens, nicht die Wahl
 * - `setSetting` — ein Merker des Geräts, z. B. was die Einführung schon gezeigt hat (`lib/intro`);
 *   er folgt einer Handlung und darf ihr Rückgängig nicht nehmen
 *
 * Die Setzer von `usePreference` und `useSetting` kommen hier nie an: Sie
 * werden von einem Hook zurückgegeben und laufen an der Schnittstelle vorbei.
 */
const KEEPS_UNDO = new Set(['setCanvasNoteMeasuredHeight', 'setCanvasNoteMeasuredSize', 'recordRewardUsage', 'setSetting', 'avatarImageUrl'])

let keeping = 0

/**
 * Ein Schreibvorgang, der einer Handlung nur nachläuft, nimmt ihr das Rückgängig nicht: Die Einführung (#323)
 * hakt nach „Verschieben › Neues Projekt“ still die Lernaufgabe ab — ⌘Z gilt danach weiter dem Verschieben.
 * Wirkt nur auf den Aufruf selbst (das Vergessen geschieht beim Aufruf, nicht beim Ende des Promises).
 */
export function keepingUndo<T>(write: () => T): T {
  keeping++
  try {
    return write()
  } finally {
    keeping--
  }
}

function isRead(name: string): boolean {
  return READ_PREFIXES.some(prefix =>
    name.startsWith(prefix) && name.length > prefix.length && name[prefix.length] === name[prefix.length]!.toUpperCase(),
  )
}

export function withUndoForget(source: DataSource): DataSource {
  // Gemerkt, damit ein Aufrufer bei jedem Zugriff denselben Griff bekommt —
  // sonst müsste ihn jeder in einem Ref halten.
  const wrapped = new Map<string, unknown>()

  return new Proxy(source, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver)
      if (typeof value !== 'function' || typeof prop !== 'string') return value
      if (isRead(prop) || KEEPS_UNDO.has(prop)) return value

      const cached = wrapped.get(prop)
      if (cached) return cached

      const guarded = (...args: unknown[]) => {
        if (keeping === 0) forgetUndo()
        // Auf dem Ziel und nicht auf dem Proxy: Ruft eine Methode intern eine
        // andere, soll das nicht ein zweites Mal vergessen lassen.
        return (value as (...a: unknown[]) => unknown).apply(target, args)
      }
      wrapped.set(prop, guarded)
      return guarded
    },
  })
}
